import { client } from './claims';

/**
 * API client for the 70+ cancellation ring-fence staff workflow.
 *
 * A client aged 70+ who cancels still has their claim cancelled, but is never
 * auto-invoiced: the charge gate suppresses the whole Stripe chain and a
 * `cancellation_ringfenced` task lands in the manual lane for the team to
 * phone. This module is the read/write half of that task's in-pane workflow
 * (`pages/lanes/CancellationRingfencePanel.tsx`).
 *
 * Reuses `claims.ts`'s axios instance rather than standing up another one --
 * both endpoints here are `/ui/claims/...`, and `payments.ts` (whose
 * `createManualCancellationPayment` the "charge anyway" action drives) already
 * imports the same instance, so a ring-fence decision goes out over exactly
 * one client with one set of auth interceptors.
 */

/**
 * `email_source` stamped on the task by the backend
 * (`ClaimCancellationService.RINGFENCE_TASK_SOURCE`). It is the ONLY honest
 * marker for these tasks -- the subject line is human-readable text that any
 * manually-created task could coincidentally match, and `emailType`/`type` are
 * plain `manual`, shared with every other manual to-do.
 */
export const RINGFENCE_TASK_SOURCE = 'cancellation_ringfenced';

/** One entry of the backend's closed waiver vocabulary. */
export interface WaiverReasonOption {
  code: string;
  label: string;
  /** True for the one code whose label promises a note (`other`). Mirrors
   * `fee_waiver_service.REASON_REQUIRING_NOTE`, so enforcing it client-side
   * can never disagree with the 400 the write path would return. */
  noteRequired: boolean;
}

/**
 * The price of charging anyway, as computed by the same
 * `stripe_service.calculate_total_amount` the suppressed auto-charge would
 * have used.
 *
 * `baseAmountPounds` is the figure to send as `amount` to the manual payment
 * route: THAT ROUTE ADDS VAT ITSELF. The ring-fence task body quotes the
 * inc-VAT total, so echoing the number a staff member can read on screen
 * would bill an elderly client VAT on VAT.
 */
export interface RingfenceFee {
  chargeableClaims: number;
  baseAmountPounds: number;
  vatAmountPounds: number;
  totalAmountPounds: number;
}

/** GET /ui/claims/fee-decision-options response. `fee` is null when nothing
 * would have been charged (a courtesy review) or pricing was unavailable. */
export interface FeeDecisionOptions {
  reasons: WaiverReasonOption[];
  fee: RingfenceFee | null;
}

export interface FeeWaiverClaimResult {
  claimId: string;
  claimReference?: string;
  basis?: string;
  updated?: boolean;
  memoRecorded?: boolean;
}

/**
 * POST /ui/claims/<id>/fee-waiver response.
 *
 * `warnings` is the field that matters most here. It is non-empty EXACTLY
 * when part of the write was lost -- a sibling claim of the same cancellation
 * that could not be updated, a group query that failed, or a saved waiver
 * whose timeline memo could not be written. The route still returns 200 in
 * those cases (the decision the staff member made IS recorded on the claim
 * they acted on), so a UI that only checks the status code shows a green tick
 * over a half-written waiver, and one of the client's three claims still
 * looks chargeable to everyone downstream. Never swallow it.
 */
export interface FeeWaiverResponse {
  message: string;
  claimId: string;
  waived: boolean;
  reason: string;
  note: string;
  basis: string;
  waivedBy: string;
  waivedAt: string;
  claimIds: string[];
  results: FeeWaiverClaimResult[];
  warnings: string[];
}

export interface FeeWaiverRequest {
  waived: boolean;
  reason: string;
  note: string;
}

/**
 * The waiver vocabulary and the fee, both from the server.
 *
 * The reason list is deliberately NOT a constant in this file. The write path
 * rejects any code outside `fee_waiver_service.WAIVER_REASONS` with a 400, so
 * a hand-typed dropdown that drifts by a single code silently stops a staff
 * member recording a phone call they have already had with a vulnerable
 * client. Fetching it means the two cannot diverge.
 *
 * @param chargeableClaims how many claims in the cancellation would have been
 *   charged (`claudeAnalysis.extractedData.wouldHaveChargedClaimIds.length`).
 *   0 (or omitted) prices nothing and returns `fee: null`.
 * @param baseAmountPounds an arbitrary EX-VAT amount to price instead of a
 *   claim count, for the general "New manual payment" modal where a staff
 *   member types their own figure. Priced by the same server-side method
 *   that bills the client, so the previewed inc-VAT total IS the charge.
 *   Omitted from the query string entirely when undefined, so the
 *   ring-fence panel's one-argument calls are unaffected.
 */
export const fetchFeeDecisionOptions = async (
  chargeableClaims = 0,
  baseAmountPounds?: number
): Promise<FeeDecisionOptions> => {
  const params: Record<string, number> = { chargeableClaims };
  if (baseAmountPounds !== undefined) {
    params.baseAmountPounds = baseAmountPounds;
  }
  const response = await client.get<FeeDecisionOptions>(
    '/ui/claims/fee-decision-options',
    { params }
  );
  return response.data;
};

/**
 * Record the fee decision against a claim.
 *
 * The waiver reaches every claim cancelled in the SAME cancellation event for
 * this client, not just the one in the URL -- which is why the response's
 * `claimIds`/`results`/`warnings` are per-claim and must be surfaced.
 *
 * `waivedBy`/`waivedAt`/`basis` are derived server-side from the authenticated
 * user and are silently ignored if sent, so they are not on the request type.
 */
export const recordFeeWaiver = async (
  claimId: string,
  payload: FeeWaiverRequest
): Promise<FeeWaiverResponse> => {
  const response = await client.post<FeeWaiverResponse>(
    `/ui/claims/${encodeURIComponent(claimId)}/fee-waiver`,
    payload
  );
  return response.data;
};

/**
 * One question of the 70+ hold-review call script, as served by
 * `GET /ui/tasks/hold-review/questions`. NEVER hard-code the wording or the
 * question count here -- Jasmine's vulnerable-client policy will change it,
 * in the backend constant (`hold_review_service.HOLD_REVIEW_QUESTIONS`), and
 * the claim-timeline memo records it as-asked.
 *
 * `answerType` decides the control the panel renders: 'choice' questions get
 * Yes/No/N-A radios (+ optional note); 'text' questions get a required
 * free-text box and MUST be sent back with `answer: null` (the server
 * rejects a yes/no/na answer on a text question). Q6's text carries the
 * literal placeholder "[email address on file]" -- the panel substitutes the
 * task's actual client email at display time, when one is present.
 */
export interface HoldReviewQuestion {
  number: number;
  text: string;
  answerType: 'choice' | 'text';
}

export type HoldReviewAnswerValue = 'yes' | 'no' | 'na';

export interface HoldReviewAnswer {
  question: number;
  /** null for a 'text' question -- the free-text response travels in `note`. */
  answer: HoldReviewAnswerValue | null;
  note?: string;
}

export type HoldReviewOutcome = 'keep' | 'cancel_charge' | 'cancel_waive';

export interface HoldReviewCompleteRequest {
  answers: HoldReviewAnswer[];
  overallNote?: string;
  outcome: HoldReviewOutcome;
  /** Required when outcome is 'cancel_waive'; one of the codes served by
   * fetchFeeDecisionOptions (the server 400s anything else). */
  waiverReason?: string;
}

/**
 * POST /ui/tasks/<id>/hold-review/complete response.
 *
 * `warnings` is non-blocking here (unlike the fee-waiver route's warnings,
 * which keep the task open) -- by the time this response comes back the
 * server has already verified the cancellation and completed the task
 * (`taskCompleted` is always true on a 200). A warning means something
 * best-effort afterwards (typically the fee-waiver record, or a per-claim
 * timeline memo) did not land and needs a manual follow-up; surface it to
 * the caller but do not treat it as a failure.
 */
export interface HoldReviewCompleteResponse {
  message: string;
  outcome: HoldReviewOutcome;
  claimIds: string[];
  cancelledClaimIds: string[];
  requiresPayment: boolean;
  warnings: string[];
  taskCompleted: boolean;
}

export const fetchHoldReviewQuestions = async (): Promise<HoldReviewQuestion[]> => {
  const response = await client.get<{ questions: HoldReviewQuestion[] }>(
    '/ui/tasks/hold-review/questions'
  );
  return response.data.questions;
};

/** Record the review call and execute its outcome. The SERVER completes the
 * task (and clears the holds) -- callers refresh via onChanged and must not
 * complete the task a second time client-side. */
export const completeHoldReview = async (
  taskId: string,
  payload: HoldReviewCompleteRequest
): Promise<HoldReviewCompleteResponse> => {
  const response = await client.post<HoldReviewCompleteResponse>(
    `/ui/tasks/${encodeURIComponent(taskId)}/hold-review/complete`,
    payload
  );
  return response.data;
};

/** One logged call attempt (Amendment 2). `at` is server UTC ISO-8601 with a
 * Z suffix -- safe for `new Date()`, no UK DD/MM ambiguity. */
export interface CallAttempt {
  by: string;
  at: string;
}

/**
 * Log one unanswered call attempt against a ring-fence task -- hold-type AND
 * legacy post-cancel alike (both are phone-call tasks). The server stamps the
 * actor from the session and the time from its own clock; nothing travels in
 * the body. Returns the FULL updated history, oldest first -- render it, so
 * two staff members logging attempts see each other's.
 */
export const recordCallAttempt = async (taskId: string): Promise<CallAttempt[]> => {
  const response = await client.post<{ callAttempts: CallAttempt[] }>(
    `/ui/tasks/${encodeURIComponent(taskId)}/call-attempts`
  );
  return response.data.callAttempts;
};

/** The `allowedReasons` a 400 from the waiver route echoes back -- the codes
 * only, so the UI can name what it should have sent. */
export const allowedReasonsFromError = (error: unknown): string[] | null => {
  const data = (error as { response?: { data?: { allowedReasons?: unknown } } })
    ?.response?.data;
  return Array.isArray(data?.allowedReasons) ? (data!.allowedReasons as string[]) : null;
};

/** The server's own error text, when it sent one. */
export const errorMessageFrom = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === 'string' && data.error ? data.error : fallback;
};

/** HTTP status of a failed request, when there was a response at all. */
export const statusFrom = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
};
